/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useIntl} from 'react-intl'
import {validatePassword} from '@salesforce/retail-react-app/app/utils/password-utils'
import {TermsAndConditionsLabel} from './terms-and-conditions-label'

export default function useRegistrationFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            placeholder: formatMessage({
                defaultMessage: 'First Name (Optional)',
                id: 'use_registration_fields.label.first_name'
            }),
            type: 'text',
            autoComplete: 'given-name',
            defaultValue: '',
            rules: {
                validate: (value) =>
                    !value ||
                    value?.trim().length ||
                    formatMessage({
                        defaultMessage: 'Please enter your first name',
                        id: 'use_registration_fields.error.required_first_name'
                    })
            },
            error: errors[`${prefix}firstName`],
            control,
            hideLabel: true
        },
        lastName: {
            name: `${prefix}lastName`,
            placeholder: formatMessage({
                defaultMessage: 'Last Name (Optional)',
                id: 'use_registration_fields.label.last_name'
            }),
            type: 'text',
            defaultValue: '',
            autoComplete: 'family-name',
            rules: {
                validate: (value) =>
                    !value ||
                    value?.trim().length ||
                    formatMessage({
                        defaultMessage: 'Please enter your last name.',
                        id: 'use_registration_fields.error.required_last_name'
                    })
            },
            error: errors[`${prefix}lastName`],
            control,
            hideLabel: true
        },
        birthDay: {
            name: `${prefix}birthDay`,
            placeholder: formatMessage({
                defaultMessage: 'Birthday (Optional)',
                id: 'use_registration_fields.label.birthday'
            }),
            type: 'date',
            defaultValue: '',
            autoComplete: 'birthDay',
            inputProps: {
                min: `${new Date().getFullYear() - 100}-01-01`,
                max: new Date().toISOString().split('T')[0],
                pr: 0,
                variant: 'calendarCustom',
                onKeyDown: (e) => {
                    const allowedKeys = ['Tab', 'Enter', 'Escape']
                    if (!allowedKeys.includes(e.key)) {
                        e.preventDefault()
                    }
                },
                onPaste: (e) => e.preventDefault()
            },
            rules: {
                validate: (value) => {
                    if (!value) return true

                    const birthDate = new Date(value)
                    const today = new Date()

                    const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000))

                    if (age < 16) {
                        return formatMessage({
                            defaultMessage: 'You must be at least 16 years old',
                            id: 'use_registration_fields.error.min_age'
                        })
                    }

                    return true
                }
            },
            error: errors[`${prefix}birthDay`],
            control
        },
        email: {
            name: `${prefix}email`,
            placeholder: formatMessage({
                defaultMessage: 'Email Address*',
                id: 'use_registration_fields.label.email'
            }),
            type: 'email',
            autoComplete: 'email',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Enter your email address',
                    id: 'use_registration_fields.error.empty_email'
                }),
                pattern: {
                    value: /^.+@.+\.[a-zA-Z]{2,3}$/,
                    message: formatMessage({
                        defaultMessage: 'Enter a valid email address such as name@email.com',
                        id: 'use_registration_fields.error.pattern_email'
                    })
                }
            },
            error: errors[`${prefix}email`],
            control,
            hideLabel: true
        },
        password: {
            name: `${prefix}password`,
            placeholder: formatMessage({
                defaultMessage: 'Password*',
                id: 'use_registration_fields.label.password'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Enter your password',
                    id: 'use_registration_fields.error.required_password'
                }),
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars ||
                        formatMessage({
                            defaultMessage:
                                'Password must be at least 8 characters long, include uppercase and lowercase letters, one special character, at least one number, and must not contain any part of your email address.',
                            id: 'password_requirements.error'
                        }),
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase ||
                        formatMessage({
                            defaultMessage:
                                'Password must be at least 8 characters long, include uppercase and lowercase letters, one special character, at least one number, and must not contain any part of your email address.',
                            id: 'password_requirements.error'
                        }),
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase ||
                        formatMessage({
                            defaultMessage:
                                'Password must be at least 8 characters long, include uppercase and lowercase letters, one special character, at least one number, and must not contain any part of your email address.',
                            id: 'password_requirements.error'
                        }),
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber ||
                        formatMessage({
                            defaultMessage:
                                'Password must be at least 8 characters long, include uppercase and lowercase letters, one special character, at least one number, and must not contain any part of your email address.',
                            id: 'password_requirements.error'
                        }),
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        formatMessage({
                            defaultMessage:
                                'Password must be at least 8 characters long, include uppercase and lowercase letters, one special character, at least one number, and must not contain any part of your email address.',
                            id: 'password_requirements.error'
                        })
                }
            },
            inputProps: {
                maxLength: 255
            },
            error: errors[`${prefix}password`],
            control,
            hideLabel: true
        },
        passwordConfirm: {
            name: `${prefix}passwordConfirm`,
            placeholder: formatMessage({
                defaultMessage: 'Confirm Password*',
                id: 'use_registration_fields.label.password.confirm'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Enter your password again',
                    id: 'use_registration_fields.error.empty_confirm_password'
                }),
                validate: {
                    isValidLength: (value) =>
                        (value.length >= 8 && value.length <= 255) ||
                        formatMessage({
                            defaultMessage: 'This field needs 8 to 255 characters.',
                            id: 'use_registration_fields.error.password_confirm_length'
                        }),
                    matchesPassword: (value, formValues) =>
                        value === formValues?.[`${prefix}password`] ||
                        formatMessage({
                            defaultMessage: 'Does not match new password',
                            id: 'use_registration_fields.error.passwords_do_not_match'
                        })
                }
            },
            error: errors[`${prefix}passwordConfirm`],
            control,
            hideLabel: true
        },
        termsAndConditions: {
            name: `${prefix}termsAndConditions`,
            label: <TermsAndConditionsLabel />,
            type: 'checkbox',
            defaultValue: false,
            rules: {
                required: formatMessage({
                    id: 'use_registration.terms.and.conditions.error',
                    defaultMessage: 'Terms and conditions must be accepted'
                })
            },
            error: errors[`${prefix}termsAndConditions`],
            control,
            inputProps: {
                alignItems: 'flex-start',
                fontSize: 'xs',
                color: 'primaryBlack'
            }
        },
        emailSubscribe: {
            name: `${prefix}emailSubscribe`,
            label: formatMessage({
                defaultMessage:
                    'Yes, I would like to receive commercial electronic messages, including a newsletter and discount notifications. I understand I can unsubscribe at any time.',
                id: 'use_registration_fields.label.sign_up_to_emails'
            }),
            type: 'checkbox',
            defaultValue: false,
            error: errors[`${prefix}emailSubscribe`],
            control,
            inputProps: {
                alignItems: 'flex-start',
                lineHeight: '15px',
                color: 'primaryBlack'
            },
            labelProps: {
                fontSize: 'xs'
            }
        }
    }

    return fields
}
